import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Shield, Lock, CheckCircle2, X, Download, Sparkles } from "lucide-react";

/** Trust badges strip — SOC 2, GDPR, TLS, uptime, stack. Renders inline. */
export function TrustStrip() {
  const items = [
    { icon: Shield, label: "SOC 2 · in progress" },
    { icon: Lock,   label: "GDPR · UK-GDPR · CCPA" },
    { icon: Lock,   label: "TLS 1.2+ · bcrypt password hashing" },
    { icon: CheckCircle2, label: "99.9% uptime target" },
    { icon: Sparkles, label: "Claude 4.5 · Native Stripe" },
  ];
  return (
    <div className="flex items-center justify-center flex-wrap gap-x-6 gap-y-2 py-4 px-4 border-y border-zinc-200/60 bg-white/40 backdrop-blur-sm" data-testid="trust-strip">
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <div key={i} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600" data-testid={`trust-badge-${i}`}>
            <Icon size={11} className="text-cyan-700"/> {it.label}
          </div>
        );
      })}
    </div>
  );
}

/** Founder track-record strip — the delivery experience Harmonizer is built on.
 * These are past engagements (pre-Capittance), not current customer logos —
 * the label is deliberately worded to reflect that. */
export function SocialProofStrip() {
  const logos = [
    "A FTSE 250 SAP programme",
    "US$1.2B S/4HANA rollout",
    "3 D365 F&O migrations",
    "EU public-sector transformation",
    "Global fintech scale-up",
  ];
  return (
    <div className="py-3 px-4" data-testid="social-proof-strip">
      <div className="flex items-center justify-center flex-wrap gap-x-6 gap-y-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600 shrink-0 mr-2">Built on delivery experience from</div>
        {logos.map((l, i) => (
          <div key={i} className="font-display text-xs font-bold text-zinc-700 tracking-tight" data-testid={`social-logo-${i}`}>{l}</div>
        ))}
      </div>
      <div className="text-center mt-2 font-mono text-[10px] text-zinc-500 max-w-2xl mx-auto" data-testid="social-proof-caption">
        The pattern Harmonizer runs on was proven across these engagements before Capittance existed.
      </div>
    </div>
  );
}

/**
 * ExitIntentModal — global "grab the free checklist before you go" modal.
 * Triggers on mouseleave from the top of the viewport (desktop) or after 40s
 * on mobile. Persists in localStorage so we only nag once per week.
 * Never renders on authenticated / transactional routes.
 */
const IGNORE_ROUTES = ["/csuite/dashboard", "/projects", "/payment", "/admin", "/share", "/csuite/verify"];
const KEY = "cap_exit_intent_shown_at";
const COOL_OFF_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function ExitIntentModal() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const suppressed = IGNORE_ROUTES.some(p => loc.pathname.startsWith(p));

  useEffect(() => {
    if (suppressed) return;
    try {
      const last = Number(localStorage.getItem(KEY) || 0);
      if (Date.now() - last < COOL_OFF_MS) return;
    } catch { /* noop */ }

    let shown = false;
    const onMouse = (e) => {
      if (shown) return;
      if (e.clientY <= 0) { shown = true; setOpen(true); markShown(); }
    };
    const mobileTimer = setTimeout(() => {
      if (!shown && window.innerWidth < 768) { shown = true; setOpen(true); markShown(); }
    }, 40000);
    document.addEventListener("mouseleave", onMouse);
    return () => { document.removeEventListener("mouseleave", onMouse); clearTimeout(mobileTimer); };
  }, [suppressed]);

  const markShown = () => { try { localStorage.setItem(KEY, String(Date.now())); } catch { /* noop */ } };
  const close = () => setOpen(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const RUNBOOK_URL = "/downloads/guides/Capittance-S4HANA-Cutover-Runbook-214.xlsx";

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/runbook`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), source: "exit-intent" }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || "Something went wrong. Please try again."); return; }
      setSent(d.sent ? "email" : "download");
    } catch {
      setErr("Network error. Please try again.");
    } finally { setBusy(false); }
  };

  if (suppressed || !open) return null;
  return (
    <div className="fixed inset-0 z-[95] grid place-items-center px-4" style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }} onClick={close}>
      <div className="bg-white rounded-sm shadow-2xl max-w-md w-full p-6 md:p-8 relative" style={{ borderTop: "4px solid #F59E0B" }} onClick={(e) => e.stopPropagation()} data-testid="exit-intent-modal">
        <button onClick={close} className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-800" data-testid="exit-intent-close"><X size={18}/></button>
        <div className="w-10 h-10 rounded-sm bg-amber-100 grid place-items-center text-amber-700 mb-3"><Download size={18}/></div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Before you go</div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight mt-1">Get the 214-step S/4HANA cutover runbook — free</h2>
        <p className="text-sm text-zinc-700 leading-relaxed mt-2">Boardroom-tested. Nine tracks by the minute. Owner and rollback path for every task. Sent instantly — no spam, no newsletter.</p>
        {!sent ? (
          <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2">
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                   placeholder="you@company.com"
                   className="flex-1 border border-[var(--border)] rounded-sm py-2.5 px-3 text-sm outline-none focus:border-amber-500" data-testid="exit-intent-email"/>
            <button type="submit" disabled={busy} className="btn btn-primary inline-flex items-center gap-1.5" data-testid="exit-intent-submit">
              <Download size={13}/> {busy ? "Sending…" : "Send it"}
            </button>
          </form>
        ) : (
          <div className="mt-4 text-sm" data-testid="exit-intent-sent">
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 size={16}/> {sent === "email" ? "Sent. Check your inbox in the next minute." : "Your runbook is ready."}</div>
            <a href={RUNBOOK_URL} download className="inline-flex items-center gap-1.5 mt-2 text-sky-700 font-semibold hover:underline" data-testid="exit-intent-download">
              <Download size={13}/> {sent === "email" ? "Or download it now (Excel)" : "Download it now (Excel)"}
            </a>
          </div>
        )}
        {err && <div className="mt-2 text-xs text-red-600" data-testid="exit-intent-error">{err}</div>}
        <div className="mt-4 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          <Link to="/resources" className="hover:text-zinc-900">Or browse all templates →</Link>
          <button onClick={close} className="hover:text-zinc-900" data-testid="exit-intent-nothanks">No thanks</button>
        </div>
      </div>
    </div>
  );
}
